import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Database, Download, Play, ShieldAlert } from "lucide-react";
import { fmtDateTime } from "@/lib/dateFormat";

/** FINANCE BATCH 2 (item 2.0) — Admin-only screen for the data correction scripts.
 *  Dry run shows the summary and downloads the full report. Apply needs a dry run from the last
 *  24 hours plus the word APPLY. The scripts themselves are unchanged. */
export default function DataScriptsPage() {
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState("");
  const [busy, setBusy] = useState("");
  const [reports, setReports] = useState({});
  const [confirmText, setConfirmText] = useState({});
  const [jobs, setJobs] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/admin/data-scripts");
      setScripts(data.data || []);
    } catch (e) {
      setDenied(formatApiError(e.response?.data?.detail) || "Admin only");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // FOLLOW-UP 1 (F5): the run happens in the background — the button returns at once and the card
  // follows the job to the end, so a long apply can never be cut off by the browser.
  const timers = useRef({});
  const pollJob = (id, jobId) => {
    if (timers.current[id]) clearInterval(timers.current[id]);
    let misses = 0;
    const timer = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/data-scripts/jobs/${jobId}`);
        const job = data.job || {};
        misses = 0;
        setJobs(j => ({ ...j, [id]: job }));
        if (job.state !== "running") {
          clearInterval(timer); delete timers.current[id];
          setBusy("");
          if (job.state === "done") {
            setReports(r => ({ ...r, [id]: job.report }));
            toast.success(job.dry_run
              ? `Dry run finished — ${job.done || 0} rows would change`
              : `Applied — ${job.done || 0} rows changed`);
          } else {
            toast.error(job.message || "The run failed");
          }
          load();
        }
      } catch {
        // iteration 101: one bad poll is not the end of the run — give it a few tries
        misses += 1;
        if (misses >= 5) { clearInterval(timer); delete timers.current[id]; setBusy(""); }
      }
    }, 2000);
    timers.current[id] = timer;
  };

  // iteration 101: a run started before a reload keeps showing on the card
  useEffect(() => {
    scripts.forEach(sc => {
      if (sc.is_running && sc.running_job && !timers.current[sc.id]) {
        setBusy(sc.id);
        setJobs(j => ({ ...j, [sc.id]: { state: "running", message: "Running…" } }));
        pollJob(sc.id, sc.running_job);
      }
    });
  }, [scripts]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => Object.values(timers.current).forEach(clearInterval), []);

  const run = async (id, dryRun) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/data-scripts/${id}/run`, {
        dry_run: dryRun, confirm: dryRun ? "" : (confirmText[id] || ""),
      });
      setConfirmText(c => ({ ...c, [id]: "" }));
      setJobs(j => ({ ...j, [id]: { state: "running", message: "Running…", dry_run: dryRun } }));
      toast.success(dryRun ? "Dry run started" : "Apply started — this screen will show the report");
      pollJob(id, data.job_id);
    } catch (e) {
      setBusy("");
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // FOLLOW-UP 2 (2b): the dispatch mismatch report downloads every row, not the first 100
  const downloadMismatchExcel = async () => {
    try {
      const r = await api.get("/admin/data-scripts/dispatch-items-mismatch/export", { responseType: "blob", timeout: 120000 });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data]));
      a.download = "ORBIT2_dispatch_mismatches.xlsx";
      a.click();
    } catch { toast.error("Could not download the file"); }
  };

  const downloadJson = (id) => {
    const blob = new Blob([JSON.stringify(reports[id], null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${id}_report.json`;
    a.click();
  };

  const downloadCsv = (id) => {
    const rep = reports[id] || {};
    const rows = rep.needs_review || rep.first_50_converted || rep.first_50_orders || rep.customers || [];
    const flat = rows.map(r => Object.fromEntries(
      Object.entries(r).map(([k, v]) => [k, typeof v === "object" ? JSON.stringify(v) : v])));
    const cols = flat.length ? Object.keys(flat[0]) : ["note"];
    const csv = [cols.join(","), ...flat.map(r => cols.map(c => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv || "no rows"], { type: "text/csv" }));
    a.download = `${id}_report.csv`;
    a.click();
  };

  if (loading) return <div className="p-6 text-sm text-zinc-500">Loading…</div>;
  if (denied) return (
    <div className="p-6" data-testid="data-scripts-denied">
      <div className="flex items-center gap-2 text-sm text-red-700"><ShieldAlert className="w-4 h-4" />{denied}</div>
    </div>
  );

  return (
    <div className="p-6 max-w-6xl" data-testid="data-scripts-page">
      <div className="flex items-center gap-2 mb-1">
        <Database className="w-5 h-5 text-zinc-700" />
        <h1 className="text-xl font-semibold text-zinc-900">Data scripts</h1>
      </div>
      <p className="text-sm text-zinc-500 mb-6">
        Corrections to existing records. Every script runs as a dry run first and changes nothing
        until you apply it. Run order after deploy: <span className="font-semibold text-zinc-700">Normalise order money units → Fill missing legacy payment rows → Relabel payment status → Read channel payment terms → Compute due dates</span> —
        the screen will not let you apply one before the one ahead of it.
      </p>

      <div className="space-y-4">
        {scripts.map(sc => (
          <div key={sc.id} className="border border-zinc-200 rounded-sm bg-white" data-testid={`script-${sc.id}`}>
            <div className="px-4 py-3 border-b border-zinc-100 flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-zinc-900">{sc.name}
                  <span className="ml-2 text-[10px] uppercase tracking-wide text-zinc-400">{sc.group}</span>
                </p>
                <p className="text-xs text-zinc-500 mt-1 max-w-3xl">{sc.description}</p>
                {sc.run_after && (
                  <p className={`text-[11px] mt-1 ${sc.run_after_applied ? "text-emerald-700" : "text-amber-700"}`}
                     data-testid={`run-after-${sc.id}`}>
                    Runs after: {sc.run_after_name} {sc.run_after_applied ? "— applied" : "— NOT applied yet, Apply is locked"}
                  </p>
                )}
                <p className="text-[11px] text-zinc-400 mt-2">
                  {sc.last_dry_run_at
                    ? `Last dry run ${fmtDateTime(sc.last_dry_run_at)} by ${sc.last_dry_run_by}`
                    : "No dry run yet"}
                  {sc.last_applied_at ? ` · Applied ${fmtDateTime(sc.last_applied_at)} by ${sc.last_applied_by}` : ""}
                </p>
                {sc.can_apply && (
                  <p className="text-[11px] text-zinc-500 mt-1" data-testid={`counts-${sc.id}`}>
                    Dry run: <b>{sc.last_dry_run_rows ?? 0}</b> rows · Applied: <b>{sc.last_applied_rows ?? 0}</b> rows
                  </p>
                )}
                {jobs[sc.id]?.state === "running" && (
                  <p className="text-[11px] text-amber-700 mt-1" data-testid={`job-running-${sc.id}`}>
                    {jobs[sc.id].message || "Running…"}
                    {jobs[sc.id].started_at ? ` · started ${fmtDateTime(jobs[sc.id].started_at)}` : ""}
                  </p>
                )}
                {jobs[sc.id]?.state === "failed" && (
                  <p className="text-[11px] text-red-700 mt-1" data-testid={`job-failed-${sc.id}`}>{jobs[sc.id].message}</p>
                )}
                {jobs[sc.id]?.state === "done" && (
                  <p className="text-[11px] text-emerald-700 mt-1" data-testid={`job-done-${sc.id}`}>{jobs[sc.id].message}</p>
                )}
              </div>
              <Button onClick={() => run(sc.id, true)} disabled={busy === sc.id}
                className="rounded-sm text-xs h-8 shrink-0" data-testid={`dry-run-${sc.id}`}>
                <Play className="w-3 h-3 mr-1" />{busy === sc.id ? "Running…" : "Dry run"}
              </Button>
            </div>

            {reports[sc.id] && (
              <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-100">
                <pre className="text-[11px] font-mono text-zinc-700 whitespace-pre-wrap max-h-48 overflow-auto"
                     data-testid={`summary-${sc.id}`}>
{JSON.stringify(sc.id === "normalise-order-money"
  ? { counts: reports[sc.id].counts, needs_review: reports[sc.id].needs_review_count }
  : sc.id === "relabel-payment-status"
  ? { changed: reports[sc.id].orders_changed, unchanged: reports[sc.id].orders_unchanged, counts: reports[sc.id].counts_old_to_new }
  : sc.id === "paid-not-closed"
  ? { customers: reports[sc.id].customers_blocked_only_by_unclosed_paid_orders, orders_to_close: reports[sc.id].orders_to_close }
  : sc.id === "verify-legacy-payments"
  ? { orders_affected: reports[sc.id].orders_affected, payment_entries: reports[sc.id].payment_entries, left_for_finance: reports[sc.id].left_for_finance_count }
  : sc.id === "legacy-amount-paid-rows"
  ? { orders_checked: reports[sc.id].orders_checked, rows_to_add: reports[sc.id].orders_affected, total_rupees: reports[sc.id].total_rupees_added, verified: reports[sc.id].rows_verified_count, unverified: reports[sc.id].rows_unverified_count, skipped_needs_review: reports[sc.id].skipped_needs_review_count, paid_less_than_rows: reports[sc.id].paid_less_than_rows_count }
  : sc.id === "fix-paise-payments"
  ? { fixed: reports[sc.id].fixed?.count ?? reports[sc.id].fixed, skipped: reports[sc.id].skipped?.count ?? reports[sc.id].skipped, not_found: reports[sc.id].not_found?.count }
  : reports[sc.id], null, 1)}
                </pre>
                <div className="flex gap-2 mt-2">
                  <Button variant="outline" size="sm" className="rounded-sm text-[11px] h-7"
                          onClick={() => downloadCsv(sc.id)} data-testid={`download-csv-${sc.id}`}>
                    <Download className="w-3 h-3 mr-1" />Download rows (CSV)
                  </Button>
                  {sc.id === "dispatch-items-mismatch" && (
                    <Button variant="outline" size="sm" className="rounded-sm text-[11px] h-7"
                            onClick={downloadMismatchExcel} data-testid="download-mismatch-excel">
                      <Download className="w-3 h-3 mr-1" />All rows (Excel)
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="rounded-sm text-[11px] h-7"
                          onClick={() => downloadJson(sc.id)} data-testid={`download-json-${sc.id}`}>
                    <Download className="w-3 h-3 mr-1" />Full report (JSON)
                  </Button>
                </div>
              </div>
            )}

            {sc.can_apply && (
              <div className="px-4 py-3 flex items-center gap-2">
                <Input value={confirmText[sc.id] || ""} placeholder="Type APPLY to confirm"
                       onChange={e => setConfirmText(c => ({ ...c, [sc.id]: e.target.value }))}
                       className="h-8 text-xs rounded-sm w-56 font-mono" data-testid={`confirm-${sc.id}`} />
                <Button variant="outline"
                        disabled={busy === sc.id || !sc.apply_unlocked
                                  || (confirmText[sc.id] || "").trim().toUpperCase() !== "APPLY"}
                        onClick={() => {
                          if ((confirmText[sc.id] || "").trim().toUpperCase() !== "APPLY") {
                            toast.error("Type APPLY to confirm."); return;
                          }
                          if (!window.confirm(`Apply "${sc.name}" to the live data? This changes records.`)) return;
                          run(sc.id, false);
                        }}
                        className="rounded-sm text-xs h-8 border-red-300 text-red-700 hover:bg-red-50"
                        data-testid={`apply-${sc.id}`}>
                  Apply
                </Button>
                {!sc.apply_unlocked && (
                  <span className="text-[11px] text-zinc-400" data-testid={`apply-locked-${sc.id}`}>
                    {sc.run_after && !sc.run_after_applied
                      ? `Apply ${sc.run_after_name} first — the run order is enforced`
                      : "Run a dry run first — an apply needs one from the last 24 hours"}
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
